import React, { useState, useEffect, useCallback } from 'react'
import api from '../services/authService'
import toast from 'react-hot-toast'
import {
  CalendarDaysIcon,
  ListBulletIcon,
  ChartBarIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline'

const DIAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']
const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
]

const STATUS_CONFIG = {
  rascunho:  { label: 'Rascunho',  bg: 'bg-gray-100',  text: 'text-gray-700',  dot: 'bg-gray-400'  },
  agendado:  { label: 'Agendado',  bg: 'bg-blue-100',  text: 'text-blue-700',  dot: 'bg-blue-500'  },
  publicado: { label: 'Publicado', bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  cancelado: { label: 'Cancelado', bg: 'bg-red-100',   text: 'text-red-700',   dot: 'bg-red-500'   },
}

const PRIORIDADE_CONFIG = {
  baixa: { label: 'Baixa', color: 'text-gray-500',  dot: 'bg-gray-400'   },
  media: { label: 'Média', color: 'text-yellow-600', dot: 'bg-yellow-400' },
  alta:  { label: 'Alta',  color: 'text-red-600',   dot: 'bg-red-500'    },
}

const TIPO_OPTIONS = [
  { value: 'post',    label: 'Post'    },
  { value: 'story',   label: 'Story'   },
  { value: 'reel',    label: 'Reel'    },
  { value: 'blog',    label: 'Blog'    },
  { value: 'email',   label: 'E-mail'  },
  { value: 'anuncio', label: 'Anúncio' },
]

const EMPTY_FORM = {
  titulo: '',
  conteudo: '',
  tipo: 'post',
  canais: [],
  data_agendada: '',
  hora_agendada: '',
  hashtags: '',
  imagem_url: '',
  link_url: '',
  categoria: '',
  prioridade: 'media',
  notas: '',
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay()
  return day === 0 ? 6 : day - 1
}

function formatDateBR(dateStr) {
  if (!dateStr) return ''
  const [y, m, d] = dateStr.split('-')
  return `${d}/${m}/${y}`
}

function isoToday() {
  return new Date().toISOString().split('T')[0]
}

export default function MarketingCalendarPage() {
  const [activeTab, setActiveTab] = useState('calendar')
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth() }
  })
  const [posts, setPosts] = useState([])
  const [monthPosts, setMonthPosts] = useState([])
  const [canais, setCanais] = useState([])
  const [categorias, setCategorias] = useState([])
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selectedDay, setSelectedDay] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [editingPost, setEditingPost] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [filters, setFilters] = useState({ status: '', canal: '', categoria: '' })

  const fetchCanais = useCallback(async () => {
    try {
      const res = await api.get('/api/marketing/calendar/canais')
      setCanais(res.data || [])
    } catch {
      setCanais([])
    }
  }, [])

  const fetchCategorias = useCallback(async () => {
    try {
      const res = await api.get('/api/marketing/calendar/categorias')
      setCategorias(res.data || [])
    } catch {
      setCategorias([])
    }
  }, [])

  const fetchMonthPosts = useCallback(async (year, month) => {
    try {
      const res = await api.get(`/api/marketing/calendar/mes/${year}/${month + 1}`)
      setMonthPosts(res.data || [])
    } catch {
      setMonthPosts([])
    }
  }, [])

  const fetchPosts = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (filters.status) params.status = filters.status
      if (filters.canal) params.canal = filters.canal
      if (filters.categoria) params.categoria = filters.categoria
      params.limite = 200
      const res = await api.get('/api/marketing/calendar/posts', { params })
      setPosts(res.data || [])
    } catch {
      toast.error('Erro ao carregar posts')
    } finally {
      setLoading(false)
    }
  }, [filters])

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/api/marketing/calendar/dashboard')
      setDashboard(res.data || null)
    } catch {
      setDashboard(null)
    }
  }, [])

  useEffect(() => {
    fetchCanais()
    fetchCategorias()
  }, [fetchCanais, fetchCategorias])

  useEffect(() => {
    fetchMonthPosts(currentDate.year, currentDate.month)
  }, [currentDate, fetchMonthPosts])

  useEffect(() => {
    if (activeTab === 'lista') fetchPosts()
    if (activeTab === 'dashboard') fetchDashboard()
  }, [activeTab, fetchPosts, fetchDashboard])

  function prevMonth() {
    setCurrentDate(prev => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 }
      return { year: prev.year, month: prev.month - 1 }
    })
    setSelectedDay(null)
  }

  function nextMonth() {
    setCurrentDate(prev => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 }
      return { year: prev.year, month: prev.month + 1 }
    })
    setSelectedDay(null)
  }

  function getPostsForDay(day) {
    const dateStr = `${currentDate.year}-${String(currentDate.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return monthPosts.filter(p => p.data_agendada && p.data_agendada.startsWith(dateStr))
  }

  function getSelectedDayPosts() {
    if (!selectedDay) return []
    return getPostsForDay(selectedDay)
  }

  function getCanalColor(canalId) {
    const c = canais.find(c => c.id === canalId || c.name === canalId)
    return c ? c.color : '#6b7280'
  }

  function getCanalName(canalId) {
    const c = canais.find(c => c.id === canalId || c.name === canalId)
    return c ? c.name : canalId
  }

  function getCategoriaName(catId) {
    const c = categorias.find(c => c.id === catId || c.name === catId)
    return c ? c.name : catId
  }

  function getCategoriaColor(catId) {
    const c = categorias.find(c => c.id === catId || c.name === catId)
    return c ? c.color : '#6b7280'
  }

  function openNewModal(day) {
    const dateStr = day
      ? `${currentDate.year}-${String(currentDate.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      : ''
    setEditingPost(null)
    setForm({ ...EMPTY_FORM, data_agendada: dateStr })
    setShowModal(true)
  }

  function openEditModal(post) {
    setEditingPost(post)
    setForm({
      titulo: post.titulo || '',
      conteudo: post.conteudo || '',
      tipo: post.tipo || 'post',
      canais: Array.isArray(post.canais) ? post.canais : [],
      data_agendada: post.data_agendada ? post.data_agendada.split('T')[0] : '',
      hora_agendada: post.hora_agendada || '',
      hashtags: post.hashtags || '',
      imagem_url: post.imagem_url || '',
      link_url: post.link_url || '',
      categoria: post.categoria || '',
      prioridade: post.prioridade || 'media',
      notas: post.notas || '',
    })
    setShowModal(true)
  }

  function closeModal() {
    setShowModal(false)
    setEditingPost(null)
    setForm(EMPTY_FORM)
  }

  function handleFormChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  function handleCanalToggle(canalId) {
    setForm(prev => {
      const exists = prev.canais.includes(canalId)
      return {
        ...prev,
        canais: exists ? prev.canais.filter(c => c !== canalId) : [...prev.canais, canalId],
      }
    })
  }

  async function handleSave(extraStatus) {
    if (!form.titulo.trim()) {
      toast.error('O título é obrigatório')
      return
    }
    setSaving(true)
    try {
      const payload = { ...form }
      if (extraStatus) payload.status = extraStatus
      if (editingPost) {
        await api.put(`/api/marketing/calendar/posts/${editingPost.id}`, payload)
        toast.success('Post atualizado com sucesso')
      } else {
        await api.post('/api/marketing/calendar/posts', payload)
        toast.success('Post criado com sucesso')
      }
      closeModal()
      fetchMonthPosts(currentDate.year, currentDate.month)
      if (activeTab === 'lista') fetchPosts()
      if (activeTab === 'dashboard') fetchDashboard()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao salvar post')
    } finally {
      setSaving(false)
    }
  }

  async function handleChangeStatus(postId, status) {
    try {
      await api.patch(`/api/marketing/calendar/posts/${postId}/status`, { status })
      toast.success('Status atualizado')
      fetchMonthPosts(currentDate.year, currentDate.month)
      if (activeTab === 'lista') fetchPosts()
      if (activeTab === 'dashboard') fetchDashboard()
    } catch {
      toast.error('Erro ao atualizar status')
    }
  }

  async function handleDelete(postId) {
    if (!window.confirm('Excluir este post?')) return
    try {
      await api.delete(`/api/marketing/calendar/posts/${postId}`)
      toast.success('Post excluído')
      fetchMonthPosts(currentDate.year, currentDate.month)
      if (activeTab === 'lista') fetchPosts()
      if (activeTab === 'dashboard') fetchDashboard()
      if (selectedDay) setSelectedDay(prev => prev)
    } catch {
      toast.error('Erro ao excluir post')
    }
  }

  const daysInMonth = getDaysInMonth(currentDate.year, currentDate.month)
  const firstDay = getFirstDayOfMonth(currentDate.year, currentDate.month)
  const todayStr = isoToday()

  const calendarCells = []
  for (let i = 0; i < firstDay; i++) calendarCells.push(null)
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d)
  while (calendarCells.length % 7 !== 0) calendarCells.push(null)

  return (
    <div className="flex flex-col h-full bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Calendário Editorial</h1>
            <p className="text-sm text-gray-500 mt-0.5">Planejamento e agendamento de conteúdo</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
              <button
                onClick={() => setActiveTab('calendar')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${activeTab === 'calendar' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <CalendarDaysIcon className="w-4 h-4" />
                Calendário
              </button>
              <button
                onClick={() => setActiveTab('lista')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${activeTab === 'lista' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <ListBulletIcon className="w-4 h-4" />
                Lista
              </button>
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${activeTab === 'dashboard' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <ChartBarIcon className="w-4 h-4" />
                Dashboard
              </button>
            </div>
            <button
              onClick={() => openNewModal(null)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Novo Post
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-auto">
        {/* TAB: CALENDARIO */}
        {activeTab === 'calendar' && (
          <div className="flex gap-4 h-full">
            <div className="flex-1 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
              {/* Month Navigation */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <h2 className="text-lg font-semibold text-gray-900">
                  {MESES[currentDate.month]} {currentDate.year}
                </h2>
                <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 border-b border-gray-100">
                {DIAS_SEMANA.map(d => (
                  <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {d}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 flex-1">
                {calendarCells.map((day, idx) => {
                  const dateStr = day
                    ? `${currentDate.year}-${String(currentDate.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                    : null
                  const isToday = dateStr === todayStr
                  const isSelected = day === selectedDay
                  const dayPosts = day ? getPostsForDay(day) : []
                  const isWeekend = idx % 7 === 5 || idx % 7 === 6

                  return (
                    <div
                      key={idx}
                      onClick={() => day && setSelectedDay(isSelected ? null : day)}
                      onDoubleClick={() => day && openNewModal(day)}
                      className={`min-h-[90px] border-b border-r border-gray-100 p-1.5 transition-colors cursor-pointer
                        ${!day ? 'bg-gray-50' : ''}
                        ${isWeekend && day ? 'bg-blue-50/30' : ''}
                        ${isSelected ? 'bg-blue-50 ring-2 ring-inset ring-blue-400' : day ? 'hover:bg-gray-50' : ''}
                      `}
                    >
                      {day && (
                        <>
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium mb-1
                            ${isToday ? 'bg-blue-600 text-white' : 'text-gray-700'}
                          `}>
                            {day}
                          </div>
                          <div className="flex flex-wrap gap-0.5">
                            {dayPosts.slice(0, 4).map((post, pi) => {
                              const firstCanal = Array.isArray(post.canais) && post.canais.length > 0 ? post.canais[0] : null
                              const color = firstCanal ? getCanalColor(firstCanal) : '#6b7280'
                              return (
                                <div
                                  key={pi}
                                  onClick={e => { e.stopPropagation(); openEditModal(post) }}
                                  title={post.titulo}
                                  className="w-full px-1.5 py-0.5 rounded text-xs font-medium truncate cursor-pointer hover:opacity-80 transition-opacity"
                                  style={{ backgroundColor: color + '22', color, border: `1px solid ${color}44` }}
                                >
                                  {post.titulo}
                                </div>
                              )
                            })}
                            {dayPosts.length > 4 && (
                              <div className="text-xs text-gray-500 px-1">+{dayPosts.length - 4} mais</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Side Panel */}
            {selectedDay && (
              <div className="w-72 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Posts do dia</p>
                    <p className="text-base font-bold text-gray-900">
                      {String(selectedDay).padStart(2, '0')} de {MESES[currentDate.month]}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openNewModal(selectedDay)}
                      className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                      title="Novo post neste dia"
                    >
                      <PlusIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setSelectedDay(null)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {getSelectedDayPosts().length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <CalendarDaysIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Nenhum post agendado</p>
                      <button
                        onClick={() => openNewModal(selectedDay)}
                        className="mt-2 text-blue-600 text-xs hover:underline"
                      >
                        Criar novo post
                      </button>
                    </div>
                  ) : (
                    getSelectedDayPosts().map(post => (
                      <SidePanelPostCard
                        key={post.id}
                        post={post}
                        canais={canais}
                        getCanalColor={getCanalColor}
                        getCanalName={getCanalName}
                        onEdit={() => openEditModal(post)}
                        onDelete={() => handleDelete(post.id)}
                        onStatusChange={status => handleChangeStatus(post.id, status)}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB: LISTA */}
        {activeTab === 'lista' && (
          <div className="space-y-4">
            {/* Filter Bar */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex flex-wrap gap-3">
                <select
                  value={filters.status}
                  onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="rounded-lg border border-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Todos os status</option>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <select
                  value={filters.canal}
                  onChange={e => setFilters(prev => ({ ...prev, canal: e.target.value }))}
                  className="rounded-lg border border-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Todos os canais</option>
                  {canais.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <select
                  value={filters.categoria}
                  onChange={e => setFilters(prev => ({ ...prev, categoria: e.target.value }))}
                  className="rounded-lg border border-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Todas as categorias</option>
                  {categorias.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <button
                  onClick={fetchPosts}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Filtrar
                </button>
                {(filters.status || filters.canal || filters.categoria) && (
                  <button
                    onClick={() => setFilters({ status: '', canal: '', categoria: '' })}
                    className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                  >
                    Limpar
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-16 text-gray-400">
                <div className="inline-block w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-sm">Carregando posts...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 text-center py-16 text-gray-400">
                <DocumentTextIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-base font-medium">Nenhum post encontrado</p>
                <button
                  onClick={() => openNewModal(null)}
                  className="mt-3 text-blue-600 text-sm hover:underline"
                >
                  Criar primeiro post
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {posts.map(post => (
                  <PostListCard
                    key={post.id}
                    post={post}
                    canais={canais}
                    getCanalColor={getCanalColor}
                    getCanalName={getCanalName}
                    getCategoriaName={getCategoriaName}
                    getCategoriaColor={getCategoriaColor}
                    onEdit={() => openEditModal(post)}
                    onDelete={() => handleDelete(post.id)}
                    onStatusChange={status => handleChangeStatus(post.id, status)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: DASHBOARD */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {!dashboard ? (
              <div className="text-center py-16 text-gray-400">
                <div className="inline-block w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-sm">Carregando dashboard...</p>
              </div>
            ) : (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  <KpiCard
                    label="Total de Posts"
                    value={dashboard.resumo?.total ?? 0}
                    icon={<DocumentTextIcon className="w-6 h-6" />}
                    color="blue"
                  />
                  <KpiCard
                    label="Rascunhos"
                    value={dashboard.resumo?.rascunhos ?? 0}
                    icon={<PencilSquareIcon className="w-6 h-6" />}
                    color="gray"
                  />
                  <KpiCard
                    label="Agendados"
                    value={dashboard.resumo?.agendados ?? 0}
                    icon={<ClockIcon className="w-6 h-6" />}
                    color="blue"
                  />
                  <KpiCard
                    label="Publicados"
                    value={dashboard.resumo?.publicados ?? 0}
                    icon={<CheckCircleIcon className="w-6 h-6" />}
                    color="green"
                  />
                  <KpiCard
                    label="Próxima Semana"
                    value={dashboard.resumo?.proxima_semana ?? 0}
                    icon={<CalendarDaysIcon className="w-6 h-6" />}
                    color="purple"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Posts por Canal */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">Posts por Canal</h3>
                    {(!dashboard.por_canal || dashboard.por_canal.length === 0) ? (
                      <p className="text-sm text-gray-400 text-center py-6">Sem dados disponíveis</p>
                    ) : (
                      <div className="space-y-3">
                        {dashboard.por_canal.map((item, idx) => {
                          const max = Math.max(...dashboard.por_canal.map(i => i.total || 0))
                          const pct = max > 0 ? ((item.total || 0) / max) * 100 : 0
                          const canalData = canais.find(c => c.id === item.canal || c.name === item.canal)
                          const color = canalData?.color || '#6b7280'
                          return (
                            <div key={idx}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-700 font-medium">{canalData?.name || item.canal || 'Sem canal'}</span>
                                <span className="text-gray-500">{item.total}</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2.5">
                                <div
                                  className="h-2.5 rounded-full transition-all duration-500"
                                  style={{ width: `${pct}%`, backgroundColor: color }}
                                ></div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {/* Posts por Categoria */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">Posts por Categoria</h3>
                    {(!dashboard.por_categoria || dashboard.por_categoria.length === 0) ? (
                      <p className="text-sm text-gray-400 text-center py-6">Sem dados disponíveis</p>
                    ) : (
                      <div className="space-y-3">
                        {dashboard.por_categoria.map((item, idx) => {
                          const max = Math.max(...dashboard.por_categoria.map(i => i.total || 0))
                          const pct = max > 0 ? ((item.total || 0) / max) * 100 : 0
                          const catData = categorias.find(c => c.id === item.categoria || c.name === item.categoria)
                          const color = catData?.color || '#6b7280'
                          return (
                            <div key={idx}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-700 font-medium">{catData?.name || item.categoria || 'Sem categoria'}</span>
                                <span className="text-gray-500">{item.total}</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2.5">
                                <div
                                  className="h-2.5 rounded-full transition-all duration-500"
                                  style={{ width: `${pct}%`, backgroundColor: color }}
                                ></div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status breakdown */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Distribuição por Status</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                      const count = dashboard.resumo?.[key === 'agendado' ? 'agendados' : key === 'publicado' ? 'publicados' : key === 'cancelado' ? 'cancelados' : 'rascunhos'] ?? 0
                      const total = dashboard.resumo?.total || 1
                      const pct = Math.round((count / total) * 100)
                      return (
                        <div key={key} className={`${cfg.bg} rounded-xl p-4`}>
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`}></div>
                            <span className={`text-xs font-semibold uppercase tracking-wide ${cfg.text}`}>{cfg.label}</span>
                          </div>
                          <p className={`text-3xl font-bold ${cfg.text}`}>{count}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{pct}% do total</p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Modal Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editingPost ? 'Editar Post' : 'Novo Post'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
              {/* Titulo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Titulo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.titulo}
                  onChange={e => handleFormChange('titulo', e.target.value)}
                  placeholder="Ex: Promoção de fim de semana"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Conteudo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Conteudo</label>
                <textarea
                  value={form.conteudo}
                  onChange={e => handleFormChange('conteudo', e.target.value)}
                  placeholder="Texto do post..."
                  rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Tipo + Prioridade */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo</label>
                  <select
                    value={form.tipo}
                    onChange={e => handleFormChange('tipo', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {TIPO_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Prioridade</label>
                  <select
                    value={form.prioridade}
                    onChange={e => handleFormChange('prioridade', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {Object.entries(PRIORIDADE_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Canais */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Canais</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {canais.map(canal => {
                    const selected = form.canais.includes(canal.id)
                    return (
                      <label
                        key={canal.id}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all select-none
                          ${selected ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => handleCanalToggle(canal.id)}
                          className="sr-only"
                        />
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: canal.color || '#6b7280' }}
                        ></div>
                        <span className="text-sm font-medium text-gray-700 truncate">{canal.name}</span>
                        {selected && (
                          <CheckCircleIcon className="w-4 h-4 text-blue-600 ml-auto flex-shrink-0" />
                        )}
                      </label>
                    )
                  })}
                  {canais.length === 0 && (
                    <p className="text-sm text-gray-400 col-span-3">Nenhum canal cadastrado</p>
                  )}
                </div>
              </div>

              {/* Data + Hora */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Data Agendada</label>
                  <input
                    type="date"
                    value={form.data_agendada}
                    onChange={e => handleFormChange('data_agendada', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Hora</label>
                  <input
                    type="time"
                    value={form.hora_agendada}
                    onChange={e => handleFormChange('hora_agendada', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Categoria</label>
                <select
                  value={form.categoria}
                  onChange={e => handleFormChange('categoria', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Sem categoria</option>
                  {categorias.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Hashtags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Hashtags</label>
                <input
                  type="text"
                  value={form.hashtags}
                  onChange={e => handleFormChange('hashtags', e.target.value)}
                  placeholder="#viploc #locação #veiculos"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Links */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">URL da Imagem</label>
                  <input
                    type="text"
                    value={form.imagem_url}
                    onChange={e => handleFormChange('imagem_url', e.target.value)}
                    placeholder="https://..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Link do Post</label>
                  <input
                    type="text"
                    value={form.link_url}
                    onChange={e => handleFormChange('link_url', e.target.value)}
                    placeholder="https://..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Notas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Notas Internas</label>
                <textarea
                  value={form.notas}
                  onChange={e => handleFormChange('notas', e.target.value)}
                  placeholder="Observacoes, instrucoes para o designer, etc."
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl gap-3 flex-wrap">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <div className="flex items-center gap-2 flex-wrap">
                {editingPost && (
                  <>
                    <button
                      disabled={saving}
                      onClick={() => handleSave('agendado')}
                      className="px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors disabled:opacity-50"
                    >
                      <ClockIcon className="w-4 h-4 inline mr-1" />
                      Agendar
                    </button>
                    <button
                      disabled={saving}
                      onClick={() => handleSave('publicado')}
                      className="px-4 py-2 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors disabled:opacity-50"
                    >
                      <CheckCircleIcon className="w-4 h-4 inline mr-1" />
                      Publicar
                    </button>
                  </>
                )}
                <button
                  disabled={saving}
                  onClick={() => handleSave(null)}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {editingPost ? 'Salvar Alteracoes' : 'Criar Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SidePanelPostCard({ post, canais, getCanalColor, getCanalName, onEdit, onDelete, onStatusChange }) {
  const status = STATUS_CONFIG[post.status] || STATUS_CONFIG.rascunho
  const prio = PRIORIDADE_CONFIG[post.prioridade] || PRIORIDADE_CONFIG.media

  return (
    <div className="border border-gray-100 rounded-xl p-3 hover:border-gray-200 transition-colors bg-white shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-semibold text-gray-900 line-clamp-2 flex-1">{post.titulo}</p>
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={onEdit} className="p-1 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors">
            <PencilSquareIcon className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors">
            <TrashIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-2">
        {Array.isArray(post.canais) && post.canais.slice(0, 3).map((c, i) => {
          const color = getCanalColor(c)
          return (
            <span
              key={i}
              className="inline-block px-1.5 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: color + '22', color }}
            >
              {getCanalName(c)}
            </span>
          )
        })}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
          {status.label}
        </span>
        <div className="flex items-center gap-1">
          <div className={`w-1.5 h-1.5 rounded-full ${prio.dot}`}></div>
          <span className={`text-xs ${prio.color}`}>{prio.label}</span>
        </div>
        {post.hora_agendada && (
          <span className="text-xs text-gray-400">{post.hora_agendada}</span>
        )}
      </div>

      {post.status !== 'publicado' && post.status !== 'cancelado' && (
        <div className="mt-2 flex gap-1 flex-wrap">
          {post.status !== 'agendado' && (
            <button
              onClick={() => onStatusChange('agendado')}
              className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
            >
              Agendar
            </button>
          )}
          <button
            onClick={() => onStatusChange('publicado')}
            className="text-xs px-2 py-0.5 rounded bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
          >
            Publicar
          </button>
        </div>
      )}
    </div>
  )
}

function PostListCard({ post, getCanalColor, getCanalName, getCategoriaName, getCategoriaColor, onEdit, onDelete, onStatusChange }) {
  const status = STATUS_CONFIG[post.status] || STATUS_CONFIG.rascunho
  const prio = PRIORIDADE_CONFIG[post.prioridade] || PRIORIDADE_CONFIG.media
  const [showStatusMenu, setShowStatusMenu] = useState(false)

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-all shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 mt-0.5">
          <div className={`w-2 h-2 rounded-full ${prio.dot} mt-2`}></div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-gray-900 truncate max-w-md">{post.titulo}</h3>
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${status.bg} ${status.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
              {status.label}
            </span>
          </div>

          {post.conteudo && (
            <p className="text-sm text-gray-500 line-clamp-1 mb-2">{post.conteudo}</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {post.data_agendada && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <CalendarDaysIcon className="w-3.5 h-3.5" />
                {formatDateBR(post.data_agendada?.split('T')[0])}
                {post.hora_agendada && ` - ${post.hora_agendada}`}
              </span>
            )}

            {Array.isArray(post.canais) && post.canais.slice(0, 4).map((c, i) => {
              const color = getCanalColor(c)
              return (
                <span
                  key={i}
                  className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: color + '22', color, border: `1px solid ${color}44` }}
                >
                  {getCanalName(c)}
                </span>
              )
            })}

            {post.categoria && (
              <span
                className="inline-block px-2 py-0.5 rounded-full text-xs font-medium border"
                style={{
                  backgroundColor: (getCategoriaColor(post.categoria) || '#6b7280') + '22',
                  color: getCategoriaColor(post.categoria) || '#6b7280',
                  borderColor: (getCategoriaColor(post.categoria) || '#6b7280') + '44',
                }}
              >
                {getCategoriaName(post.categoria)}
              </span>
            )}

            {post.tipo && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full capitalize">
                {post.tipo}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <div className="relative">
            <button
              onClick={() => setShowStatusMenu(prev => !prev)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              title="Mudar status"
            >
              <ExclamationCircleIcon className="w-4 h-4" />
            </button>
            {showStatusMenu && (
              <div className="absolute right-0 top-8 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 w-36">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <button
                    key={key}
                    onClick={() => { onStatusChange(key); setShowStatusMenu(false) }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${post.status === key ? 'font-semibold' : ''}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
                    {cfg.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-500 hover:text-blue-600 transition-colors">
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

function KpiCard({ label, value, icon, color }) {
  const colorMap = {
    blue:   { bg: 'bg-blue-50',   text: 'text-blue-600',   icon: 'text-blue-500'   },
    green:  { bg: 'bg-green-50',  text: 'text-green-600',  icon: 'text-green-500'  },
    gray:   { bg: 'bg-gray-50',   text: 'text-gray-600',   icon: 'text-gray-500'   },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', icon: 'text-purple-500' },
    red:    { bg: 'bg-red-50',    text: 'text-red-600',    icon: 'text-red-500'    },
  }
  const c = colorMap[color] || colorMap.gray

  return (
    <div className={`${c.bg} rounded-xl p-5 flex flex-col gap-3`}>
      <div className={`${c.icon}`}>{icon}</div>
      <div>
        <p className={`text-3xl font-bold ${c.text}`}>{value}</p>
        <p className="text-sm text-gray-500 mt-0.5">{label}</p>
      </div>
    </div>
  )
}
